import { useQuery } from "@tanstack/react-query";
import { Outlet } from "react-router-dom";

import { getPlatformCurrent } from "api/methods";

/**
 * Sign-in, sign-up and onboarding shell for tablet and desktop.
 *
 * A phone gives these screens the whole display, so the form alone fills it and
 * feels complete. On a wide screen a lone form is a fragment floating in empty
 * space — the fix is not to make the form wider (it is the right width already)
 * but to give the rest of the viewport a job.
 *
 * From `desk` the practice's own home artwork and headline fill a brand panel
 * beside the form, so signing in already looks like the practice you are about
 * to walk into, and the imagery carries straight through to the home page.
 *
 * The panel deliberately shows no logo: every screen in this flow already
 * renders the practice mark above its form, and two marks on one screen is the
 * duplication this layout exists to avoid.
 */
export const AuthShellLayout = () => {
  const { data: platform } = useQuery({
    ...getPlatformCurrent.keyed(),
    queryFn: () => getPlatformCurrent().catch(() => null),
    staleTime: Infinity,
  });

  const heroImage = platform?.homeImageUrl;
  const headline = platform?.homeTitle;
  const subhead = platform?.homeSubtitle;

  // With no artwork configured the panel falls back to the practice's brand
  // colour rather than a stock image that is not theirs.
  const panelStyle = heroImage
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.1) 45%, rgba(0,0,0,0.55) 100%), url("${heroImage}")`,
      }
    : undefined;

  const hasPanelContent = !!heroImage || !!headline;

  return (
    <div className="flex min-h-screen flex-col desk:flex-row">
      {hasPanelContent && (
        <aside
          aria-hidden
          className="relative hidden shrink-0 bg-brand-color bg-cover bg-center desk:sticky desk:top-0 desk:block desk:h-screen desk:w-[45%] deskxl:w-1/2"
          style={panelStyle}
        >
          {(headline || subhead) && (
            <div className="absolute inset-x-0 bottom-0 p-8 text-white desklg:p-14">
              {headline && (
                <p className="font-serif text-h2 text-shadow desklg:max-w-lg desklg:text-[52px] desklg:leading-[1.05] deskxl:text-[60px]">
                  {headline}
                </p>
              )}
              {subhead && (
                <p className="pt-3 text-body2 text-shadow desklg:max-w-md desklg:text-body1">
                  {subhead}
                </p>
              )}
            </div>
          )}
        </aside>
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col desk:items-center desk:bg-white">
        <div className="flex w-full flex-col desk:flex-1 desk:[&>*]:flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
